'use client';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import Button, { ButtonSize, ButtonType } from '../../button/button';
import styles from './onBoardingItem.module.scss';

export const OnBoardingItem = ({
  titles,
  ctaText,
  stepAmount,
  currentStep,
  animationDone,
  ctaClick,
  changeStepClick,
}: {
  titles: string[];
  ctaText: string;
  stepAmount: number;
  currentStep: number;
  animationDone: boolean;
  ctaClick: () => void;
  changeStepClick: (step: number) => void;
}) => {
  const [stepInit, setStepInit] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoUrls = [
    "", // empty for first image
    "https://cdn.suitsupply.com/video/upload/v1731660557/video/suitsupply/custom-made/Onboarding_SS_01.webm",
    "https://cdn.suitsupply.com/video/upload/v1731660559/video/suitsupply/custom-made/Onboarding_SS_02.webm",
    "https://cdn.suitsupply.com/video/upload/v1731660562/video/suitsupply/custom-made/Onboarding_SS_03.webm",
    "https://cdn.suitsupply.com/video/upload/v1731660568/video/suitsupply/custom-made/Onboarding_SS_04.webm"
  ];

  useEffect(() => {
    setStepInit(true);
  });

  useEffect(() => {
    videoRef.current?.load();
  }, [currentStep])

  return (
    <div className={cn(styles.container, styles['position_' + currentStep])} data-test-id="onBoardingContainer">
      <div className={styles.videoContainer}>
        {currentStep > 0 &&
          <video ref={videoRef} className={styles.videoItem} autoPlay muted playsInline>
            <source src={videoUrls[currentStep]} type="video/webm" />
          </video>
        }
      </div>
      <div
        className={cn(styles.leftPanel, { [styles.disabled]: currentStep === 0 })}
        onClick={() => {
          if (currentStep !== 0) {
            changeStepClick(currentStep - 1);
          }
        }}
      ></div>
      <div
        className={cn(styles.rightPanel, { [styles.disabled]: stepAmount <= currentStep + 1 })}
        onClick={() => {
          if (stepAmount > currentStep + 1) {
            changeStepClick(currentStep + 1);
          }
        }}
      ></div>
      <div
        className={cn(styles.slideArrow, styles.slidePrev, {
          [styles.disabled]: currentStep === 0,
        })}
        onClick={() => {
          if (currentStep !== 0) {
            changeStepClick(currentStep - 1);
          }
        }}
      >
        Previous
      </div>
      <div
        className={cn(styles.slideArrow, styles.slideNext, {
          [styles.disabled]: stepAmount === currentStep + 1,
        })}
        onClick={() => {
          if (stepAmount > currentStep + 1) {
            changeStepClick(currentStep + 1);
          }
        }}
      >
        Next
      </div>
      <div className={styles.content}>
        {currentStep === 0 && (
          <div className={styles.logoContainer}>
            <i></i> Custom Made
          </div>
        )}
        {currentStep > 0 && <div className={styles.stepNo}>0{currentStep}</div>}
        <div className={styles.titleContainer}>
          {[...Array(stepAmount)].map((stepNo, index) => (
            <div
              key={`titles_${index}`}
              className={cn(styles.title, {[styles.previousTitle]: index < currentStep, [styles.nextTitle]: index > currentStep, [styles.currentTitle]: index === currentStep})}
            >{titles[index]}</div>
          ))}
        </div>
        <div className={styles.stepContainer}>
          {[...Array(stepAmount)].map((stepNo, index) => (
            <span
              key={`dots_${index}`}
              className={cn(
                styles.dot,
                { [styles.noAnimation]: currentStep === stepAmount - 1 },
                { [styles.selected]: currentStep === index && stepInit },
                { [styles.selectedBefore]: currentStep > index || animationDone },
                { [styles.selectedDone]: animationDone || (currentStep === stepAmount - 1 && currentStep === index)}
              )}
            ></span>
          ))}
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <Button
          onClick={ctaClick}
          type={ButtonType.Primary}
          isFullSize={true}
          size={ButtonSize.Regular}
          testId="onboarding-start-designing"
        >
          <img
            className={styles.sizeIcon}
            src="https://cdn.suitsupply.com/image/upload/svg/icon-custom-made.svg"
            alt="Custom made"
          />
          {ctaText}
        </Button>
      </div>
    </div>
  );
};
